/* eslint-disable react/no-array-index-key */
import React, { useRef } from 'react';
import { shape, arrayOf, string } from 'prop-types';
import { FieldArray } from 'formik';
import { Image, notification } from 'antd';
import Dialog from 'Modules/Dialog/Dialog';
import AddMedicineModal from 'Modules/Prescription/AddMedicineModal/AddMedicineModal';
import AddMedicineModalOtter from 'Modules/Prescription/AddMedicineModalOtter/AddMedicineModal';
import AddMedicineModalLifepack from 'Modules/Prescription/AddMedicineModalLifepack/AddMedicineModal';
import { useDispatch, useSelector } from 'react-redux';
import { PHARMACY_SOURCE_OTTER, PHARMACY_SOURCE_LIFEPACK } from 'Constants/consults';
import {
  searchOtterSgProducts,
  withTeleconsultPriceList,
} from 'Redux/modules/webChat/prescription/actions';
import { getOtterClinicalTermLabel } from 'Helpers/consult-utils';
import IconWarning from 'Assets/icons/ic_warning.svg';
import IconSuccess from 'Assets/icons/checked-circle.svg';
import { CirclePlus } from 'lucide-react';

import styles from '../PrescriptionForm.less';

import MedicineDetails from './MedicineDetails/MedicineDetails';
import SearchTemplate from './SearchTemplate';
import { getProductFromTemplateItem } from './helpers';

const ITEM_SOURCE_ALPS = 'ALPS';
const ITEM_TYPE_MEDICINE = 'medicine';
const ITEM_TYPE_DRUG_MIXTURE = 'drug-mixture';
const ALLOWED_ITEM_TYPES = [ITEM_TYPE_MEDICINE, ITEM_TYPE_DRUG_MIXTURE];

const AdvancedFieldArray = props => {
  const {
    values: { medicines },
    templates,
    selectedPharmacy,
    currencySymbol,
    serviceId,
  } = props;

  const dispatch = useDispatch();

  const { clinicalTerm } = useSelector(state => state.webChat.prescription);
  const uniqueIdRef = useRef(0);

  // Generate unique ID for each medicine item
  const generateUniqueId = () => {
    uniqueIdRef.current += 1;
    return `medicine-${Date.now()}-${uniqueIdRef.current}`;
  };

  const { medicineDosageUnit, medicineRouteOfAdmin, medicineFrequency, durationUnit } =
    clinicalTerm || {};

  const taxPercent = selectedPharmacy?.taxProfile?.taxPercent || 0;
  const pharmacySource = selectedPharmacy.source;
  const displayTemplateSelection = pharmacySource === PHARMACY_SOURCE_OTTER;

  const handleEdit = (arrayHelpers, value, index) => {
    arrayHelpers.replace(index, {
      ...value,
      uniqueKey: value.uniqueKey || generateUniqueId(),
      mode: 'edit',
    });
  };

  const handleRemove = (arrayHelpers, index) => {
    Dialog.info({
      title: 'Confirmation',
      content: 'Are you sure you want to remove this medicine',
      callback: () => {
        arrayHelpers.remove(index);
      },
    });
  };

  const handleCancelEditting = (arrayHelpers, medicine, index) => {
    if (medicine.mode === 'add') {
      arrayHelpers.remove(index);
    }

    if (medicine.mode === 'edit') {
      arrayHelpers.replace(index, {
        ...medicine,
        mode: 'view',
      });
    }
  };

  const handleSaveEditting = (arrayHelpers, newValue, index) => {
    arrayHelpers.replace(index, {
      ...newValue,
      mode: 'view',
    });
  };
  const handleSaveEdittingOtterSgItem = (arrayHelpers, newValue, index) => {
    arrayHelpers.replace(index, {
      ...newValue,
      durationUnit: getOtterClinicalTermLabel(newValue.durationUnitCode, durationUnit),
      frequency: getOtterClinicalTermLabel(newValue.frequencyCode, medicineFrequency),
      doseUnit: getOtterClinicalTermLabel(newValue.doseUnitCode, medicineDosageUnit),
      route: getOtterClinicalTermLabel(newValue.routeCode, medicineRouteOfAdmin),
      mode: 'view',
    });
  };

  const handleIncreaseQuantity = (arrayHelpers, currentValue, index) => {
    arrayHelpers.replace(index, {
      ...currentValue,
      quantity: +currentValue.quantity + 1,
    });
  };

  const handleDecreaseQuantity = (arrayHelpers, currentValue, index) => {
    if (currentValue.quantity === 1) {
      return;
    }

    arrayHelpers.replace(index, {
      ...currentValue,
      quantity: +currentValue.quantity - 1,
    });
  };
  const handleSetQuantity = (arrayHelpers, currentValue, index, value) => {
    arrayHelpers.replace(index, {
      ...currentValue,
      quantity: value,
    });
  };

  const handleAddItem = arrayHelpers => {
    arrayHelpers.push({
      uniqueKey: generateUniqueId(),
      mode: 'add',
      description: '',
      amountPerTime: '',
      frequency: '',
      frequencyCode: '',
      quantity: 1,
      note: '',
      timeUnit: '',
      doseUnit: '',
      doseUnitCode: '',
      useCustomInstruction: false,
      uomUnit: '',
    });
  };

  const handleSelectTemplate = async (arrayHelpers, template) => {
    if (!template || !template.orderTemplateItems?.length) return;

    const orderTemplateItems = template?.orderTemplateItems || [];
    const isMedicine = item =>
      ALLOWED_ITEM_TYPES.includes(item.type) && item.source !== ITEM_SOURCE_ALPS;

    // filter out non-medicine items and ALPS items
    const needValidateIds = orderTemplateItems.filter(isMedicine).map(item => item.inventoryItemId);

    const inventoryRes = await dispatch(
      searchOtterSgProducts(
        withTeleconsultPriceList(
          {
            tenantId: selectedPharmacy.tenantId,
            productIds: needValidateIds.join(','),
            status: 'active',
            pageSize: 1000,
          },
          serviceId,
        ),
      ),
    );

    const inventoryItems = inventoryRes?.data || [];

    const validItems = [];
    const invalidItems = [];

    orderTemplateItems.forEach(orderTemplateItem => {
      const valid = inventoryItems.find(
        i =>
          isMedicine(orderTemplateItem) &&
          i.id === orderTemplateItem.inventoryItemId &&
          !!i.stockDetails?.length,
      );

      if (valid) {
        validItems.push(orderTemplateItem);
      } else {
        invalidItems.push(orderTemplateItem);
      }
    });

    if (!validItems.length) {
      notification.open({
        message: <span className={styles.notiTitle}>Template not applied</span>,
        description: 'All items are invalid and skipped.',
        placement: 'bottomLeft',
        icon: <Image src={IconWarning} alt="Warning" className={styles.notiIcon} />,
      });

      return;
    }

    // Now you can use arrayHelpers to add items to the form
    validItems.forEach(validItem => {
      arrayHelpers.push({
        uniqueKey: generateUniqueId(),
        mode: 'view',
        ...getProductFromTemplateItem(validItem, taxPercent),
      });
    });

    const countValidItems = validItems.length;
    const totalItems = orderTemplateItems.length;

    notification.open({
      message: <span className={styles.notiTitle}>Template applied</span>,
      description:
        countValidItems === totalItems
          ? `${countValidItems} items added.`
          : `${countValidItems} out of ${totalItems} items added. Invalid items skipped.`,
      placement: 'bottomLeft',
      icon: <Image src={IconSuccess} alt="Success" className={styles.notiIcon} />,
    });
  };

  return (
    <FieldArray
      name="medicines"
      render={arrayHelpers => {
        return (
          <div className={styles.medicineListWrapper}>
            {displayTemplateSelection && (
              <div className={styles.templateSelection}>
                <SearchTemplate
                  handleSelect={template => handleSelectTemplate(arrayHelpers, template)}
                  tenantId={selectedPharmacy.tenantId}
                  serviceId={serviceId}
                />
              </div>
            )}

            <div className={styles.label}>Items</div>

            <div className={styles.insertMedicine} onClick={() => handleAddItem(arrayHelpers)}>
              <CirclePlus className={styles.icon} size={16} color="#0984E3" />
              <span className={styles.text}>Add Item</span>
            </div>

            {medicines.map((medicine, index) => (
              <div
                key={medicine.uniqueKey || `medicine-${index}`}
                className={styles.medicineWrapper}>
                {medicine?.description && (
                  <MedicineDetails
                    details={medicine}
                    onEdit={() => handleEdit(arrayHelpers, medicine, index)}
                    onRemove={() => handleRemove(arrayHelpers, index)}
                    onIncreaseQuantity={() => handleIncreaseQuantity(arrayHelpers, medicine, index)}
                    onDecreaseQuantity={() => handleDecreaseQuantity(arrayHelpers, medicine, index)}
                    onSetQuantity={value => handleSetQuantity(arrayHelpers, medicine, index, value)}
                    currencySymbol={currencySymbol}
                  />
                )}
                {(medicine.mode === 'edit' || medicine.mode === 'add') && (
                  <div>
                    {pharmacySource === PHARMACY_SOURCE_OTTER && (
                      // Case Ottersg
                      <AddMedicineModalOtter
                        pharmacyId={selectedPharmacy.id}
                        pharmacy={selectedPharmacy}
                        initialValues={medicine}
                        templates={templates}
                        onCancel={() => handleCancelEditting(arrayHelpers, medicine, index)}
                        onSave={newValue =>
                          handleSaveEdittingOtterSgItem(arrayHelpers, newValue, index)
                        }
                        onAddNew={() => handleAddItem(arrayHelpers)}
                        currencySymbol={currencySymbol}
                        serviceId={serviceId}
                      />
                    )}

                    {pharmacySource === PHARMACY_SOURCE_LIFEPACK && (
                      // Case Lifepack
                      <AddMedicineModalLifepack
                        pharmacyId={selectedPharmacy.id}
                        pharmacy={selectedPharmacy}
                        initialValues={medicine}
                        templates={templates}
                        onCancel={() => handleCancelEditting(arrayHelpers, medicine, index)}
                        onSave={newValue =>
                          handleSaveEdittingOtterSgItem(arrayHelpers, newValue, index)
                        }
                        onAddNew={() => handleAddItem(arrayHelpers)}
                        currencySymbol={currencySymbol}
                      />
                    )}

                    {![PHARMACY_SOURCE_OTTER, PHARMACY_SOURCE_LIFEPACK].includes(
                      pharmacySource,
                    ) && (
                      // Case Gloco
                      <AddMedicineModal
                        pharmacyId={selectedPharmacy.id}
                        pharmacy={selectedPharmacy}
                        initialValues={medicine}
                        templates={templates}
                        onCancel={() => handleCancelEditting(arrayHelpers, medicine, index)}
                        onSave={newValue => handleSaveEditting(arrayHelpers, newValue, index)}
                        onAddNew={() => handleAddItem(arrayHelpers)}
                        currencySymbol={currencySymbol}
                        serviceId={serviceId}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }}
    />
  );
};

AdvancedFieldArray.propTypes = {
  values: shape({}).isRequired,
  selectedPharmacy: shape({}).isRequired,
  templates: arrayOf(shape({})).isRequired,
  currencySymbol: string.isRequired,
  serviceId: string,
};

export default AdvancedFieldArray;
